@if (challenge$ | async; as challenge) {
  @let status = status$ | async;

  <div
    class="flex flex-col"
    style="min-width: 360px; max-width: 420px; padding: 24px">
    <h2 class="title m-0 mb-1">Confirm it's you</h2>

    <!-- Server-owned description, identical to the one in the OTP email -->
    @if (challenge.scopeHint) {
      <div class="mb-4">
        <p class="body-2 text-default font-medium">{{ challenge.scopeHint }}</p>
      </div>
    }

    <!-- Channel comes from the challenge: a user with an authenticator gets
         TOTP and no email is sent. -->
    @if (challenge.method === "TOTP") {
      <p class="body-2 text-secondary mb-4">
        Enter the 6-digit code from your authenticator app.
      </p>
    } @else {
      <p class="body-2 text-secondary mb-4">
        We sent a code to
        <span class="font-medium text-default">{{
          challenge.destination
        }}</span>
      </p>
    }

    @if (status === "requesting") {
      <div class="flex items-center justify-center py-6">
        <mat-spinner diameter="32" />
        <p class="ml-3 text-secondary">Sending code…</p>
      </div>
    } @else if (status === "awaiting_code" || status === "verifying") {
      <!-- OTP digit inputs -->
      <div class="w-full flex gap-2 mb-4">
        @for (digit of digits; track $index) {
          <input
            #digitInput
            type="text"
            inputmode="numeric"
            maxlength="1"
            [value]="digit"
            (input)="onDigitInput($index, $event)"
            (keydown)="onDigitKeydown($index, $event)"
            (paste)="onDigitPaste($event)"
            [attr.aria-label]="'Digit ' + ($index + 1)"
            class="w-10 h-12 text-center text-lg font-semibold border-2 border-divider !border-black-light rounded-lg bg-foreground text-default focus:border-primary-600 focus:outline-none transition-colors"
            [disabled]="status === 'verifying'" />
        }
      </div>

      <!-- Resend — only email codes can be re-sent -->
      <div class="mb-6">
        @if (challenge.method === "TOTP") {
          <p class="caption text-secondary">
            The code changes every 30 seconds in your authenticator app.
          </p>
        } @else if (resendCountdown > 0) {
          <p class="caption text-secondary">
            Didn't get it? Resend in
            <span class="font-medium text-default">
              {{ resendCountdown }}s
            </span>
          </p>
        } @else {
          <button
            mat-button
            type="button"
            class="caption"
            (click)="resend()"
            [disabled]="status === 'verifying'">
            Resend code
          </button>
        }
      </div>
    } @else if (status === "failed") {
      <div
        class="flex items-center justify-center py-6 bg-red-50 border border-red-200 rounded-lg">
        <p class="text-sm text-red-700">
          {{ (error$ | async) ?? "Could not send a verification code." }}
        </p>
      </div>
    }

    <!-- Actions -->
    <div class="flex items-center justify-end gap-2">
      <button mat-button type="button" (click)="cancel()">Cancel</button>
      @if (status === "failed") {
        <!-- No `disabled` guard: this branch only renders while status is
             "failed", and a resend moves it off "failed" — so the button is
             gone before it could ever be disabled. -->
        <button
          mat-raised-button
          color="primary"
          type="button"
          (click)="resend()">
          Send a new code
        </button>
      } @else {
        <button
          mat-raised-button
          color="primary"
          type="button"
          (click)="submit()"
          [disabled]="digits.join('').length < 6 || status === 'verifying'">
          <span class="flex items-center gap-2">
            @if (status === "verifying") {
              <mat-spinner diameter="20" />
            }
            Confirm
          </span>
        </button>
      }
    </div>
  </div>
}
